/**
 * ItemList strict validators: only the members doc/classes/ItemList.xml lists
 * without `overrides=` (so not `clip_contents` or `focus_mode`), since the
 * NODE_BASE_TYPES walk delivers inherited keys and a re-declared key shadows one.
 */
import '../control/linterParser.js';
import { validatorRegistry } from '../../../../linter/ValidatorRegistry.js';
import { OVERRUN_BEHAVIOR_NAMES } from '../../../../godot/text/textServerEnums.js';
import { propertyListFamilyEntry } from '../../../../linter/validators/indexedFamily.js';
import { ITEM_LIST_ITEMS } from '../../../../godot/classdb/propertyListFamilies.js';
import { v } from '../../../../linter/validators/index.js';
import { ITEM_LIST_ICON_MODE_NAMES, ITEM_LIST_SCROLL_HINT_MODE_NAMES, ITEM_LIST_SELECT_MODE_NAMES, } from '../../../../godot/scene/gui.js';
/**
 * `item_<idx>/<leaf>` leaves: the four `base_property_helper.register_property`
 * adds (item_list.cpp:2463-2466), not `ADD_PROPERTY`. ItemList forwards
 * `_set`/`_get`/`_get_property_list` to the helper (item_list.h).
 */
const ITEM_LEAVES = {
    // `PropertyListHelper::_call_setter` forwards `(index, value)`, and each setter
    // assigns past an `ERR_FAIL_INDEX` on the index, so no leaf has a value bound.
    // item_list.cpp:2463, Variant::STRING, no hint. set_item_text
    // (item_list.cpp:89-105) assigns past the index guard.
    text: v.quotedString('text'),
    // item_list.cpp:2464, Variant::OBJECT, PROPERTY_HINT_RESOURCE_TYPE "Texture2D".
    // set_item_icon (item_list.cpp:206-228) assigns past the index guard. Without
    // PROPERTY_USAGE_STORE_IF_NULL the saver omits an unset icon, but `null` still loads.
    icon: v.resourceReference('icon'),
    // item_list.cpp:2465, Variant::BOOL, no hint. set_item_selectable
    // (item_list.cpp:368-376) assigns past the index guard. Defaults to true, so
    // it is only ever written when an item is made unselectable.
    selectable: v.boolean('selectable'),
    // item_list.cpp:2466, Variant::BOOL, no hint. set_item_disabled
    // (item_list.cpp:384-397) assigns past the index guard.
    disabled: v.boolean('disabled'),
};
const itemFamilyEntry = propertyListFamilyEntry(ITEM_LIST_ITEMS, {
    leaves: ITEM_LEAVES,
    unknownCode: 'INVALID_ITEM_KEY',
    describes: `${ITEM_LIST_ITEMS.prefix}<index>/<leaf> (see item_list.cpp, PropertyListHelper-backed)`,
    // The setters fold a negative index from the end (`p_idx += get_item_count()`),
    // but the helper refuses it first (property_list_helper.cpp:58), so that line grounds this.
    negativeIndex: {
        cite: 'property_list_helper.cpp:58',
        code: 'INVALID_ITEM_INDEX',
        message: (index) => `Item index ${index} must be non-negative. ItemList's property helper refuses a negative index (property_list_helper.cpp:58), so this property is never applied`,
    },
});
validatorRegistry.registerAll('ItemList', itemFamilyEntry, {
    // item_list.cpp:2393, Variant::BOOL, no hint. set_allow_reselect
    // (item_list.cpp:2081-2083) is a bare assignment.
    allow_reselect: v.boolean('allow_reselect'),
    // item_list.cpp:2394, Variant::BOOL, no hint. set_allow_rmb_select
    // (item_list.cpp:2073-2075) is a bare assignment.
    allow_rmb_select: v.boolean('allow_rmb_select'),
    // item_list.cpp:2395, Variant::BOOL, no hint. set_allow_search
    // (item_list.cpp:2089-2091) is a bare assignment.
    allow_search: v.boolean('allow_search'),
    // item_list.cpp:2397, Variant::BOOL, no hint. set_auto_width
    // (item_list.cpp:2152-2161) assigns past an equality early-out.
    auto_width: v.boolean('auto_width'),
    // item_list.cpp:2398, Variant::BOOL, no hint. set_auto_height
    // (item_list.cpp:2167-2176) assigns past an equality early-out.
    auto_height: v.boolean('auto_height'),
    // item_list.cpp:2400, Variant::BOOL, no hint. set_wraparound_items
    // (item_list.cpp:2197-2205) assigns past an equality early-out.
    wraparound_items: v.boolean('wraparound_items'),
    // item_list.cpp:2402, Variant::BOOL, no hint. set_tile_scroll_hint
    // (item_list.cpp:2224-2231) assigns past an equality early-out.
    tile_scroll_hint: v.boolean('tile_scroll_hint'),
    // item_list.cpp:2406, Variant::BOOL, no hint. set_same_column_width
    // (item_list.cpp:604-612) assigns past an equality early-out.
    same_column_width: v.boolean('same_column_width'),
    // item_list.cpp:2392, PROPERTY_HINT_ENUM "Single,Multi,Toggle" (0-2).
    // set_select_mode (item_list.cpp:657-665) assigns past an equality early-out
    // with no ERR_FAIL, so the hint constrains the inspector widget only: hinted.
    select_mode: v.enumInt('select_mode', ITEM_LIST_SELECT_MODE_NAMES, { hinted: 'item_list.cpp:2392' }),
    // item_list.cpp:2399, PROPERTY_HINT_ENUM with 7 entries (0-6).
    // set_text_overrun_behavior (item_list.cpp:2182-2191) assigns straight
    // through, so both ends are hinted.
    text_overrun_behavior: v.enumInt('text_overrun_behavior', OVERRUN_BEHAVIOR_NAMES, {
        hinted: 'item_list.cpp:2399',
    }),
    // item_list.cpp:2401, PROPERTY_HINT_ENUM "Disabled,Both,Top,Bottom" (0-3).
    // set_scroll_hint_mode (item_list.cpp:2211-2218) assigns straight through.
    scroll_hint_mode: v.enumInt('scroll_hint_mode', ITEM_LIST_SCROLL_HINT_MODE_NAMES, {
        hinted: 'item_list.cpp:2401',
    }),
    // item_list.cpp:2409, PROPERTY_HINT_ENUM "Top,Left" (0-1). Unlike the three
    // enums above, set_icon_mode opens with ERR_FAIL_INDEX((int)p_mode, 2)
    // (item_list.cpp:672), which drops the write entirely: enforced, not hinted.
    icon_mode: v.enumInt('icon_mode', ITEM_LIST_ICON_MODE_NAMES, { enforced: 'item_list.cpp:672' }),
    // item_list.cpp:2396, PROPERTY_HINT_RANGE "1,10,1,or_greater". The trailing
    // `or_greater` opens the max end, so the hinted 10 is not a bound; the floor
    // is ERR_FAIL_COND(p_lines < 1) at item_list.cpp:619, which drops the write.
    max_text_lines: v.int('max_text_lines', { min: 1, enforced: 'item_list.cpp:619' }),
    // item_list.cpp:2403, ADD_ARRAY_COUNT, which binds PROPERTY_HINT_NONE with no
    // hint string at all (class_db.cpp:1492). set_item_count opens with
    // ERR_FAIL_COND(p_count < 0) at item_list.cpp:527; there is no ceiling.
    item_count: v.int('item_count', { min: 0, enforced: 'item_list.cpp:527' }),
    // item_list.cpp:2405, PROPERTY_HINT_RANGE "0,10,1,or_greater", max end open.
    // ERR_FAIL_COND(p_amount < 0) at item_list.cpp:641. 0 is the legal "as many
    // columns as fit" value, not a missing setting.
    max_columns: v.int('max_columns', { min: 0, enforced: 'item_list.cpp:641' }),
    // item_list.cpp:2407, PROPERTY_HINT_RANGE "0,100,1,or_greater,suffix:px",
    // max end open. ERR_FAIL_COND(p_size < 0) at item_list.cpp:589.
    fixed_column_width: v.int('fixed_column_width', { min: 0, enforced: 'item_list.cpp:589' }),
    // item_list.cpp:2410, Variant::FLOAT with PROPERTY_HINT_NONE, so a negative or
    // fractional scale is stored as written. The one guard is
    // ERR_FAIL_COND(!Math::is_finite(p_scale)) at item_list.cpp:2098, which
    // `finite` reports for `inf`, `inf_neg` and `nan`.
    icon_scale: v.float('icon_scale', { finite: 'item_list.cpp:2098' }),
    // item_list.cpp:2411, Variant::VECTOR2I, PROPERTY_HINT_NONE ("suffix:px" only).
    // set_fixed_icon_size (item_list.cpp:691-699) assigns past an equality
    // early-out, so neither component has a floor: shape only.
    fixed_icon_size: v.vector2i('fixed_icon_size'),
    // Not an ADD_PROPERTY: `ItemList::_set`'s deprecated `items = [text, icon,
    // disabled, …]` fallback (item_list.cpp:2242-2259). Its own arity guard
    // (`arr.size() % 3`) is a sibling-index concern the format layer does not
    // reach, so only the Array-literal shape is checked here.
    items: v.arrayLiteral('items'),
});
//# sourceMappingURL=linterParser.js.map